import { createApp } from 'vue'
import App from './App.vue'
import router from './router';

import { IonicVue } from '@ionic/vue';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

/**
 * Ionic Dark Mode
 * -----------------------------------------------------
 * For more info, please see:
 * https://ionicframework.com/docs/theming/dark-mode
 */

/* @import '@ionic/vue/css/palettes/dark.always.css'; */
/* @import '@ionic/vue/css/palettes/dark.class.css'; */
// import '@ionic/vue/css/palettes/dark.system.css';

/* Theme variables */
import './tailwind.css'
import './theme/variables.scss'
/* Fonts ship with the app. Loaded from Google Fonts, a failed first load left
   habit icons as their names ("water_drop") until a restart (COACHIO-1939). */
import '@fontsource/poppins/300.css'
import '@fontsource/poppins/400.css'
import '@fontsource/poppins/500.css'
import '@fontsource/poppins/600.css'
import '@fontsource/poppins/700.css'
import '@fontsource/poppins/800.css'
import '@fontsource/poppins/900.css'
import 'material-icons/iconfont/filled.css'
import 'material-icons/iconfont/round.css'
import {createPinia} from 'pinia';
import { VueQueryPlugin, queryClient, initQueryClient, clientPersister, clientPersisterOnSuccess } from './lib/queryClient';
// Removed registerServiceWorker import - using vite-plugin-pwa instead
// toastr
import Vue3Toastify from "vue3-toastify"
import { toastOptions } from "./config/toast"
import "vue3-toastify/dist/index.css"
// After the library's own stylesheet on purpose: it restates the same
// properties, so ours has to come last to win.
import "./theme/toast.css"

import { i18n, initializeLocale } from './i18n'
import { Preferences } from '@capacitor/preferences';
import { USER_CACHE_KEY } from '@/services/AuthService';
import {
    applyBrandColor,
    applyDistributionAccent,
    brandColorFromCachedUser,
} from '@/utils/brandTheme';
import { Capacitor } from '@capacitor/core';
import { StatusBar } from '@capacitor/status-bar';
import { DISTRIBUTION } from '@/config/distribution';
import { applyBrandFont } from '@/utils/brandFont';
import { appBrandColor, loadSignedOutBranding } from '@/utils/signedOutBranding';

// Establish the compiled distribution palette before Vue renders. A locked
// white-label app keeps this palette even if a cached user carries another
// tenant color.
applyBrandColor(DISTRIBUTION.primaryColor);
applyDistributionAccent(DISTRIBUTION.accentColor);
// Same moment for the heading font: a brand without one changes nothing.
applyBrandFont(DISTRIBUTION.fontHeading);

// iOS only
window.addEventListener('statusTap', function () {
    console.log('statusbar tapped');
});

const pinia = createPinia();

// Native platform styling
if (Capacitor.isNativePlatform()) {
    if (Capacitor.getPlatform() === 'ios') {
        StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
    }
}

const app = createApp(App)
    .use(IonicVue)
    .use(pinia)
    .use(router)
    .use(i18n)
    .use(VueQueryPlugin, { queryClient, clientPersister, clientPersisterOnSuccess })
    .use(Vue3Toastify, toastOptions);

// Wire connectivity/focus to Capacitor + start query-cache persistence.
initQueryClient();

// `/login?tenant=<uuid>` from the invitation's confirmation page: the signed-out
// screens wear that workspace's brand (COACHIO-1942). App.vue applies it on arrival.
loadSignedOutBranding(window.location.search);

initializeLocale().then(() => {
    router.isReady().then(async () => {
        // White-label: apply the cached tenant brand color BEFORE first paint so a
        // branded tenant never flashes the default Coachio theme on cold start.
        // The App.vue watcher keeps it in sync once the fresh user payload loads.
        try {
            const { value } = await Preferences.get({ key: USER_CACHE_KEY });
            const cachedTenantColor = DISTRIBUTION.allowTenantColor
                ? brandColorFromCachedUser(value)
                : null;
            applyBrandColor(cachedTenantColor ?? appBrandColor(false, null));
        } catch {
            // Ignore: the watcher will apply branding once the user resolves.
        }
        app.mount('#app');
    })
});
